import React, { useEffect, useState, useMemo } from 'react';
import api from '../api';
import { getDriverScope } from '../permissions';
import StatTile from '../components/StatTile';
import { Truck, Package, CheckCircle2, Percent, TrendingUp, XCircle, RotateCcw, AlertTriangle } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const fmt = (n) => Number(n||0).toLocaleString();
const fmtKm = (n) => Number(n||0).toFixed(1);
const fmtTime = (mins) => {
  if (!mins) return '—';
  const h = Math.floor(mins/60);
  const m = Math.round(mins%60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const DRIVER_TYPE_OPTIONS = [
  ['', 'All Types'],
  ['delivery', 'Delivery Driver'],
  ['presalesman', 'Presalesman'],
  ['van_salesman', 'Van Salesman'],
];

const driverScope = getDriverScope();
const visibleTypeOptions = driverScope
  ? DRIVER_TYPE_OPTIONS.filter(([val]) => val === '' || driverScope.includes(val))
  : DRIVER_TYPE_OPTIONS;

export default function Analytics() {
  const today = new Date().toISOString().split('T')[0];
  const firstDay = new Date(new Date().setDate(1)).toISOString().split('T')[0];
  const [from, setFrom] = useState(firstDay);
  const [to, setTo] = useState(today);
  const [tab, setTab] = useState('daily');
  const [daily, setDaily] = useState(null);
  const [range, setRange] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [selDriver, setSelDriver] = useState('');
  const [selType, setSelType] = useState('');

  useEffect(() => {
    api.get('/drivers').then(r => setDrivers(r.data));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams({ date: today });
    if (selType) params.append('type', selType);
    api.get('/analytics/dashboard?' + params).then(r => setDaily(r.data));
  }, [today, selType]);

  useEffect(() => {
    const params = new URLSearchParams({ from, to });
    if (selDriver) params.append('driver_id', selDriver);
    if (selType) params.append('type', selType);
    api.get('/analytics/range?' + params).then(r => setRange(r.data));
  }, [from, to, selDriver, selType]);

  const t = daily?.totals || {};

  const dailySeries = useMemo(() => {
    const map = {};
    (range?.daily || []).forEach(r => {
      const d = String(r.session_date).split('T')[0];
      if (!map[d]) map[d] = { date: d, Invoices: 0, Delivered: 0 };
      map[d].Invoices += Number(r.invoices || 0);
      map[d].Delivered += Number(r.delivered || 0);
    });
    return Object.values(map)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(d => ({ ...d, date: new Date(d.date).toLocaleDateString('en-US', { month:'short', day:'numeric' }) }));
  }, [range]);

  const presets = [
    { label: 'Today', from: today, to: today },
    { label: 'This week', from: new Date(new Date().setDate(new Date().getDate()-7)).toISOString().split('T')[0], to: today },
    { label: 'This month', from: firstDay, to: today },
    { label: 'Last 3 months', from: new Date(new Date().setMonth(new Date().getMonth()-3)).toISOString().split('T')[0], to: today },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: 0 }}>Analytics</h1>
          <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>Daily, weekly and monthly performance</p>
        </div>
      </div>

      {/* Tabs + Driver Type filter */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', padding: 4, borderRadius: 10, width: 'fit-content' }}>
          {['daily','range'].map(t2 => (
            <button key={t2} onClick={() => setTab(t2)}
              style={{ padding: '7px 18px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                background: tab === t2 ? '#fff' : 'transparent',
                color: tab === t2 ? '#1e293b' : '#64748b',
                boxShadow: tab === t2 ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
              {t2 === 'daily' ? "Today's Summary" : 'Date Range Report'}
            </button>
          ))}
        </div>
        <select value={selType} onChange={e => setSelType(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, color: '#334155' }}>
          {visibleTypeOptions.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
        </select>
      </div>

      {tab === 'daily' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 14, marginBottom: 24 }}>
            {[
              [Truck, 'Drivers', t.total_sessions||0, '#0284c7'],
              [Package, 'Invoices', t.total_invoices||0, '#7c3aed'],
              [CheckCircle2, 'Delivered', t.delivered||0, '#16a34a'],
              [Percent, 'Success', t.total_invoices ? Math.round((t.delivered/t.total_invoices)*100)+'%' : '0%', '#ea580c'],
              [XCircle, 'Cancelled', t.cancelled||0, '#dc2626'],
              [RotateCcw, 'Rescheduled', t.rescheduled||0, '#7c3aed'],
              [AlertTriangle, 'Still Pending', t.outstanding_rescheduled||0, '#f59e0b'],
              [TrendingUp, 'Value', 'AED '+fmt(t.total_value), '#0f172a'],
            ].map(([Icon,label,value,color]) => (
              <StatTile key={label} icon={Icon} label={label} value={value} color={color} />
            ))}
          </div>

          <div style={{ background:'#fff', borderRadius:12, boxShadow:'0 1px 3px rgba(0,0,0,0.08)', overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid #f1f5f9', fontSize:14, fontWeight:600, color:'#1e293b' }}>
              Driver performance — {new Date().toLocaleDateString()}
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {['Driver','Status','Stops','Done','Pending','Value','KM','Duration','Efficiency'].map(h=>(
                    <th key={h} style={{ padding:'10px 14px', textAlign:'left', color:'#64748b', fontWeight:500, fontSize:12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(daily?.sessions||[]).map(s => {
                  const eff = s.total_stops ? Math.round((s.completed_stops/s.total_stops)*100) : 0;
                  const dur = s.route_duration_mins ? fmtTime(Math.round(s.route_duration_mins)) : '—';
                  return (
                    <tr key={s.id} style={{ borderTop:'1px solid #f1f5f9' }}>
                      <td style={{ padding:'12px 14px', fontWeight:500, color:'#1e293b' }}>{s.driver_name}</td>
                      <td style={{ padding:'12px 14px' }}>
                        <span style={{ padding:'3px 8px', borderRadius:20, fontSize:11, fontWeight:500,
                          background: s.status==='completed'?'#dcfce7':s.status==='in_progress'?'#dbeafe':'#fef9c3',
                          color: s.status==='completed'?'#16a34a':s.status==='in_progress'?'#1d4ed8':'#854d0e' }}>
                          {s.status.replace('_',' ')}
                        </span>
                      </td>
                      <td style={{ padding:'12px 14px', color:'#475569' }}>{s.total_stops}</td>
                      <td style={{ padding:'12px 14px', color:'#16a34a', fontWeight:500 }}>{s.completed_stops}</td>
                      <td style={{ padding:'12px 14px', color:'#dc2626' }}>{s.total_stops-s.completed_stops}</td>
                      <td style={{ padding:'12px 14px', color:'#1e293b' }}>AED {fmt(s.total_value)}</td>
                      <td style={{ padding:'12px 14px', color:'#475569' }}>{fmtKm(s.actual_km)} km</td>
                      <td style={{ padding:'12px 14px', color:'#475569' }}>{dur}</td>
                      <td style={{ padding:'12px 14px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ flex:1, height:6, background:'#f1f5f9', borderRadius:3, overflow:'hidden' }}>
                            <div style={{ width:eff+'%', height:'100%', borderRadius:3,
                              background: eff>=80?'#16a34a':eff>=50?'#f59e0b':'#dc2626' }} />
                          </div>
                          <span style={{ fontSize:11, color:'#475569', minWidth:30 }}>{eff}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!daily?.sessions?.length && (
                  <tr><td colSpan={9} style={{ padding:32, textAlign:'center', color:'#94a3b8' }}>No data for today</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'range' && (
        <>
          {/* Filters */}
          <div style={{ background:'#fff', borderRadius:12, padding:16, boxShadow:'0 1px 3px rgba(0,0,0,0.08)', marginBottom:20, display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
            <div style={{ display:'flex', gap:6 }}>
              {presets.map(p => (
                <button key={p.label} onClick={() => { setFrom(p.from); setTo(p.to); }}
                  style={{ padding:'6px 12px', borderRadius:20, border:'1px solid #e2e8f0', fontSize:12, cursor:'pointer',
                    background: from===p.from&&to===p.to ? '#0284c7' : '#f8fafc',
                    color: from===p.from&&to===p.to ? '#fff' : '#475569' }}>
                  {p.label}
                </button>
              ))}
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <input type="date" value={from} onChange={e=>setFrom(e.target.value)}
                style={{ padding:'6px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:12 }} />
              <span style={{ color:'#94a3b8' }}>to</span>
              <input type="date" value={to} onChange={e=>setTo(e.target.value)}
                style={{ padding:'6px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:12 }} />
            </div>
            <select value={selDriver} onChange={e=>setSelDriver(e.target.value)}
              style={{ padding:'7px 12px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:12 }}>
              <option value="">All drivers</option>
              {drivers
                .filter(d => !driverScope || driverScope.includes(d.type))
                .filter(d => !selType || d.type === selType)
                .map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          {/* Summary cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:14, marginBottom:20 }}>
            {(range?.summary||[]).map(s => {
              const eff = s.total_invoices ? Math.round((s.delivered/s.total_invoices)*100) : 0;
              return (
                <div key={s.driver_id} style={{ background:'#fff', borderRadius:12, padding:18, boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
                  <div style={{ fontWeight:700, color:'#1e293b', fontSize:14, marginBottom:10 }}>{s.driver_name}</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    {[
                      ['Days worked', s.total_days],
                      ['Invoices', s.total_invoices],
                      ['Delivered', s.delivered],
                      ['Success', eff+'%'],
                      ['Cancelled', s.cancelled||0],
                      ['Rescheduled', s.rescheduled||0],
                      ['Still Pending', s.outstanding_rescheduled||0],
                      ['KM driven', fmtKm(s.total_km)+' km'],
                      ['Avg hours', (s.avg_hours_per_day||0)+'h/day'],
                      ['Avg invoices/day', s.avg_invoices_per_day||0],
                      ['Avg value/day', 'AED '+fmt(s.avg_value_per_day||0)],
                    ].map(([label,val]) => (
                      <div key={label}>
                        <div style={{ fontSize:10, color:'#94a3b8' }}>{label}</div>
                        <div style={{ fontSize:14, fontWeight:600, color:'#1e293b' }}>{val}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop:12 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#64748b', marginBottom:4 }}>
                      <span>Efficiency</span><span>{eff}%</span>
                    </div>
                    <div style={{ height:6, background:'#f1f5f9', borderRadius:3, overflow:'hidden' }}>
                      <div style={{ width:eff+'%', height:'100%', borderRadius:3, background: eff>=80?'#16a34a':eff>=50?'#f59e0b':'#dc2626' }} />
                    </div>
                  </div>
                  <div style={{ marginTop:8, fontSize:11, color:'#475569', fontWeight:600 }}>
                    AED {fmt(s.total_value)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Charts */}
          <div style={{ background:'#fff', borderRadius:12, boxShadow:'0 1px 3px rgba(0,0,0,0.08)', overflow:'hidden', marginBottom:20 }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid #f1f5f9', fontSize:14, fontWeight:600, color:'#1e293b' }}>
              Deliveries over time
            </div>
            <div style={{ padding:16 }}>
              {dailySeries.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={dailySeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize:11, fill:'#94a3b8' }} />
                    <YAxis tick={{ fontSize:11, fill:'#94a3b8' }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize:12 }} />
                    <Bar dataKey="Invoices" fill="#7c3aed" radius={[4,4,0,0]} />
                    <Bar dataKey="Delivered" fill="#16a34a" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ padding:28, textAlign:'center', color:'#94a3b8', fontSize:13 }}>No data for this period</div>
              )}
            </div>
          </div>

          {!selDriver && (
            <div style={{ background:'#fff', borderRadius:12, boxShadow:'0 1px 3px rgba(0,0,0,0.08)', overflow:'hidden', marginBottom:20 }}>
              <div style={{ padding:'16px 20px', borderBottom:'1px solid #f1f5f9', fontSize:14, fontWeight:600, color:'#1e293b' }}>
                Delivered by driver
              </div>
              <div style={{ padding:16 }}>
                {(range?.summary||[]).length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={(range?.summary||[]).map(s => ({ name:s.driver_name, Delivered:Number(s.delivered||0), Invoices:Number(s.total_invoices||0) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="name" tick={{ fontSize:11, fill:'#94a3b8' }} />
                      <YAxis tick={{ fontSize:11, fill:'#94a3b8' }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize:12 }} />
                      <Bar dataKey="Delivered" fill="#16a34a" radius={[4,4,0,0]} />
                      <Bar dataKey="Invoices" fill="#7c3aed" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ padding:28, textAlign:'center', color:'#94a3b8', fontSize:13 }}>No data</div>
                )}
              </div>
            </div>
          )}

          {/* Daily breakdown */}
          <div style={{ background:'#fff', borderRadius:12, boxShadow:'0 1px 3px rgba(0,0,0,0.08)', overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid #f1f5f9', fontSize:14, fontWeight:600, color:'#1e293b' }}>
              Daily breakdown
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {['Date','Driver','Invoices','Delivered','Value','KM','Start','End','Duration','Status'].map(h=>(
                    <th key={h} style={{ padding:'10px 14px', textAlign:'left', color:'#64748b', fontWeight:500, fontSize:12, whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(range?.daily||[]).map((r,i) => (
                  <tr key={i} style={{ borderTop:'1px solid #f1f5f9' }}>
                    <td style={{ padding:'11px 14px', color:'#475569' }}>{new Date(r.session_date).toLocaleDateString()}</td>
                    <td style={{ padding:'11px 14px', fontWeight:500, color:'#1e293b' }}>{r.driver_name}</td>
                    <td style={{ padding:'11px 14px', color:'#475569' }}>{r.invoices}</td>
                    <td style={{ padding:'11px 14px', color:'#16a34a', fontWeight:500 }}>{r.delivered}</td>
                    <td style={{ padding:'11px 14px', color:'#1e293b' }}>AED {fmt(r.value)}</td>
                    <td style={{ padding:'11px 14px', color:'#475569' }}>{fmtKm(r.km)} km</td>
                    <td style={{ padding:'11px 14px', color:'#475569', fontSize:12 }}>{r.started_at ? new Date(r.started_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '—'}</td>
                    <td style={{ padding:'11px 14px', color:'#475569', fontSize:12 }}>{r.completed_at ? new Date(r.completed_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '—'}</td>
                    <td style={{ padding:'11px 14px', color:'#475569' }}>{fmtTime(r.actual_duration)}</td>
                    <td style={{ padding:'11px 14px' }}>
                      <span style={{ padding:'3px 8px', borderRadius:20, fontSize:11, fontWeight:500,
                        background: r.status==='completed'?'#dcfce7':r.status==='in_progress'?'#dbeafe':'#fef9c3',
                        color: r.status==='completed'?'#16a34a':r.status==='in_progress'?'#1d4ed8':'#854d0e' }}>
                        {r.status.replace('_',' ')}
                      </span>
                    </td>
                  </tr>
                ))}
                {!range?.daily?.length && (
                  <tr><td colSpan={10} style={{ padding:32, textAlign:'center', color:'#94a3b8' }}>No data for selected range</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
